import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  GridActionsCellItem,
  GridCellParams,
  GridColDef,
  GridRowId,
} from '@mui/x-data-grid-pro';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { Task } from '../../../data/models/task.model';
import TaskDialog from './TaskDialog';
import * as lookupSlice from '../../../data/lookup.slice';
import * as monitoringSlice from '../../../data/monitoring.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';

const PREFIX = 'WorkQueueTable';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
};

const StyledPaper = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    flexGrow: 1,
    alignItems: 'flex-end',
    gap: theme.spacing(2),
  },

  [`& .${classes.grid}`]: {
    width: '100%',
    backgroundColor: theme.palette.background.default,
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },
}));

enum ReasonType {
  ESCALATE,
  RESOLVE,
}

interface WorkQueueTableProps {
  tasks: Task[];
  loading: boolean;
}

export default function WorkQueueTable({
  tasks,
  loading,
}: WorkQueueTableProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();

  const escalateReasons = useAppSelector(lookupSlice.selectTaskEscalateReasons);
  const resolveReasons = useAppSelector(lookupSlice.selectTaskResolveReasons);

  const [reasonType, setReasonType] = React.useState<ReasonType>();
  const [currentTask, setCurrentTask] = React.useState<Task>();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogTitle, setDialogTitle] = React.useState('');
  const [dialogMessage, setDialogMessage] = React.useState('');
  const [reasons, setReasons] = React.useState<any[]>([]);
  const [reasonBCAttribute, setReasonBCAttribute] = React.useState('');
  const [reasonNameAttribute, setReasonNameAttribute] = React.useState('');

  const renderDetails = (params: GridCellParams) => {
    // TODO: this probably needs some thought on what to display for different kinds of events.
    const task: Task = params.row;
    return (
      <>
        {t('workQueue.tripID')}: {task.eventPayload?.tripLegReferenceID}
      </>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'alertDescription',
      headerName: t('workQueue.taskDescription'),
      flex: 1,
      sortable: false,
    },
    {
      field: 'details',
      headerName: t('workQueue.taskDetails'),
      flex: 1,
      sortable: false,
      renderCell: renderDetails,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }: { id: GridRowId }) => {
        return [
          <GridActionsCellItem
            icon={
              <Tooltip title="Resolve task">
                <CheckCircleIcon />
              </Tooltip>
            }
            label="Resolve"
            className="textPrimary"
            onClick={handleResolveClick(id)}
            color="success"
          />,
          <GridActionsCellItem
            icon={
              <Tooltip title="Escalate task">
                <PriorityHighIcon />
              </Tooltip>
            }
            label="Delete"
            className="textPrimary"
            onClick={handleEscalateClick(id)}
            color="error"
          />,
        ];
      },
    },
  ];

  const handleResolveClick = (id: GridRowId) => () => {
    const task = tasks.find((t) => t.taskUUID === id);
    if (task) {
      setCurrentTask(task);
      setReasonType(ReasonType.RESOLVE);
      setDialogTitle(t('workQueue.resolveTaskTitle'));
      setDialogMessage(t('workQueue.resolveTaskMessage'));
      setReasons(resolveReasons);
      setReasonBCAttribute('taskResolveReasonBC');
      setReasonNameAttribute('taskResolveReasonName');
      setDialogOpen(true);
    }
  };

  const handleEscalateClick = (id: GridRowId) => () => {
    const task = tasks.find((t) => t.taskUUID === id);
    if (task) {
      setCurrentTask(task);
      setReasonType(ReasonType.ESCALATE);
      setDialogTitle(t('workQueue.escalateTaskTitle'));
      setDialogMessage(t('workQueue.escalateTaskMessage'));
      setReasons(escalateReasons);
      setReasonBCAttribute('taskEscalateReasonBC');
      setReasonNameAttribute('taskEscalateReasonName');
      setDialogOpen(true);
    }
  };

  const closeDialog = (result: { ReasonBCs: string[] } | null) => {
    setDialogOpen(false);
    if (currentTask && result) {
      switch (reasonType) {
        case ReasonType.ESCALATE:
          dispatch(
            monitoringSlice.escalateTaskAsync(
              currentTask.taskUUID,
              result.ReasonBCs,
            ),
          );
          break;
        case ReasonType.RESOLVE:
          dispatch(
            monitoringSlice.resolveTaskAsync(
              currentTask.taskUUID,
              result.ReasonBCs,
            ),
          );
          break;
      }
    }
  };

  return (
    <StyledPaper className={classes.container}>
      <DataGridPro
        getRowId={(row) => row.taskUUID}
        rows={tasks}
        columns={columns}
        pagination={false}
        autoHeight={true}
        loading={loading}
        sortingMode="server"
        className={classes.grid}
        checkboxSelection={false}
        disableColumnMenu={true}
        disableColumnSelector={true}
        hideFooter={true}
      />
      <TaskDialog
        title={dialogTitle}
        message={dialogMessage}
        reasons={reasons}
        reasonBCAttribute={reasonBCAttribute}
        reasonNameAttribute={reasonNameAttribute}
        open={dialogOpen}
        close={closeDialog}
      />
    </StyledPaper>
  );
}
